/* eslint-disable react/no-unescaped-entities */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Copy,
  Check,
  ExternalLink,
  FileSearch,
  Globe,
  Repeat,
  BarChart3,
  ChevronRight,
  AlertTriangle,
  Trash2,
} from "lucide-react";
import SEO from "@/components/SEO";
import { SITE_URL } from "@/lib/seo";

/**
 * SEO Search Console Checklist — internal ops page.
 *
 * Guides Marc through the 4 GSC steps needed after publishing new/updated
 * content: (1) resubmit sitemap, (2) request indexation for 8 URLs (CA+ES),
 * (3) verify hreflang mapping, (4) weekly monitoring.
 *
 * All URLs are canonical (ai-metabolic.com). Copy buttons enable fast
 * paste-into-GSC workflow. Page is noindex + not linked from site.
 */

const GSC_BASE = "https://search.google.com/search-console";
const GSC_PROPERTY_QUERY = "resource_id=https%3A%2F%2Fai-metabolic.com%2F";

const SITEMAP_URL = `${SITE_URL}/sitemap.xml`;

const NEW_URLS = [
  {
    title: "Cluster · Sobrepès (activation)",
    ca: `${SITE_URL}/blog/com-comencar-a-moure-amb-sobrepes`,
    es: `${SITE_URL}/es/blog/como-empezar-a-moverte-con-sobrepeso`,
    priority: "P0",
    note: "Article amb HowTo schema — pot guanyar rich snippet ràpid",
  },
  {
    title: "Cluster · Vergonya (shame)",
    ca: `${SITE_URL}/blog/em-fa-vergonya-anar-al-gimnas`,
    es: `${SITE_URL}/es/blog/me-da-verguenza-ir-al-gimnasio`,
    priority: "P0",
    note: "Territori emocional únic — poca competència directa",
  },
  {
    title: "Cluster · Abandó (preparation)",
    ca: `${SITE_URL}/blog/per-que-abandono-sempre-el-canvi`,
    es: `${SITE_URL}/es/blog/por-que-abandono-siempre-el-cambio`,
    priority: "P0",
    note: "Angle psicològic — alta intenció de cerca",
  },
  {
    title: "Pilar · Activació (actualitzat)",
    ca: `${SITE_URL}/blog/com-activar-te-quan-fa-anys-que-no-et-mous`,
    es: `${SITE_URL}/es/blog/como-activarte-cuando-llevas-anos-sin-moverte`,
    priority: "P1",
    note: "Ja indexat però amb dateModified nou + FAQ nou",
  },
];

const WEEKLY_QUERIES_TO_WATCH = {
  activation: [
    "com començar a moure'm si tinc sobrepès",
    "empezar a moverse con sobrepeso",
    "ejercicio para personas con obesidad",
    "cómo empezar a hacer ejercicio si estoy gordo",
  ],
  shame: [
    "em fa vergonya anar al gimnàs",
    "me da vergüenza ir al gimnasio",
    "vergüenza cuerpo gimnasio",
    "no me atrevo a hacer ejercicio",
  ],
  preparation: [
    "per què abandono sempre",
    "por qué abandono siempre",
    "abandonar dieta ejercicio",
    "empezar y no seguir",
  ],
  pillar: [
    "activarme llevo años sin moverme",
    "activar-me fa anys que no em moc",
    "no sé por dónde empezar a moverme",
    "trampa del lunes empezar",
  ],
};

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* fallback silent */ }
  };
  return (
    <button
      onClick={onClick}
      data-testid={`copy-${label}`}
      className={`inline-flex items-center gap-1.5 text-xs font-sans-display font-medium tracking-wide px-2.5 py-1.5 rounded-md border transition ${
        copied
          ? "border-[#B4C5A4] bg-[#B4C5A4]/10 text-[#0B2545]"
          : "border-slate-200 bg-white text-[#475569] hover:border-[#0B2545] hover:text-[#0B2545]"
      }`}
      aria-label={`Copy ${label}`}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copiat" : "Copiar"}
    </button>
  );
}

function StepCard({ n, icon: Icon, title, children, testid }) {
  return (
    <section
      data-testid={testid}
      className="bg-white border border-slate-200 rounded-2xl p-7 sm:p-9 shadow-[0_2px_20px_-8px_rgba(11,37,69,0.08)]"
    >
      <div className="flex items-center gap-4 mb-5">
        <div className="h-11 w-11 rounded-full bg-[#0B2545] flex items-center justify-center flex-shrink-0">
          <span className="font-sans-display font-semibold text-[#F9F8F6] text-sm">{n}</span>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#B4C5A4] font-sans-display font-semibold mb-0.5">
            Pas {n} · Search Console
          </p>
          <h2 className="font-sans-display text-xl sm:text-2xl text-[#0B2545] tracking-tight leading-tight flex items-center gap-2">
            <Icon className="h-5 w-5 opacity-70" />
            {title}
          </h2>
        </div>
      </div>
      <div className="space-y-4 text-[#475569] leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}

function UrlRow({ url, label, inspectable = false }) {
  // Google Search Console does NOT support deep-linking a specific URL into
  // the URL Inspection tool (any ?url= or ?id= param returns 404). The most
  // reliable UX is: copy the URL to clipboard AND open the inspector in a
  // new tab, so the user only needs to paste (Cmd+V).
  const inspectorHref = `https://search.google.com/search-console/inspect?resource_id=${encodeURIComponent("https://ai-metabolic.com/")}`;
  const onInspect = async (e) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copiada — enganxa-la (Cmd+V) a la barra de dalt de GSC");
    } catch { /* clipboard may be blocked; fallback to just opening */ }
    window.open(inspectorHref, "_blank", "noopener,noreferrer");
  };
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-slate-100 last:border-none">
      <div className="min-w-0 flex-1">
        {label && (
          <span className="inline-block text-[9px] font-sans-display font-semibold uppercase tracking-widest text-[#B4C5A4] mb-0.5">
            {label}
          </span>
        )}
        <p className="font-mono text-xs text-[#0F172A] truncate">{url}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {inspectable && (
          <button
            type="button"
            onClick={onInspect}
            data-testid={`inspect-${label || "url"}`}
            className="inline-flex items-center gap-1.5 text-xs font-sans-display font-medium tracking-wide px-2.5 py-1.5 rounded-md border border-[#B4C5A4] bg-[#B4C5A4]/10 text-[#0B2545] hover:bg-[#B4C5A4]/25 transition"
          >
            Copiar i obrir GSC
            <ExternalLink className="h-3 w-3" />
          </button>
        )}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-[#475569] hover:text-[#0B2545] transition"
          aria-label="Open URL in new tab"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
        <CopyButton value={url} label={url.slice(-30)} />
      </div>
    </div>
  );
}

export default function InsightsSeoChecklist() {
  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#0F172A] font-body" data-testid="seo-checklist-page">
      <SEO
        title="SEO Checklist · AI METABOLIC"
        description="Checklist Search Console"
        path="/insights/seo"
        noindex
      />

      <header className="border-b border-slate-200 bg-white/50 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0B2545] transition"
            data-testid="back-to-insights"
          >
            <ArrowLeft className="h-4 w-4" />
            Insights
          </Link>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#B4C5A4] font-sans-display font-semibold">
            Internal · No indexat
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        {/* Intro */}
        <div className="mb-12">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#B4C5A4] font-sans-display font-semibold mb-3">
            SEO · Search Console
          </p>
          <h1 className="font-sans-display text-3xl sm:text-4xl lg:text-5xl text-[#0B2545] tracking-tight leading-[1.05] mb-4">
            Checklist per accelerar la indexació dels 3 clusters nous.
          </h1>
          <p className="text-[#475569] leading-relaxed text-base sm:text-lg max-w-2xl">
            Segueix aquests 4 passos després de cada redeploy. Els 3 primers els fas un cop. El 4t (monitoratge) es fa cada dilluns.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-[#0B2545]/5 border border-[#0B2545]/10 rounded-full text-xs text-[#0B2545] font-sans-display">
            <span className="h-1.5 w-1.5 rounded-full bg-[#B4C5A4]" />
            Temps total: ~15 minuts els passos 1-3, ~5 minuts cada dilluns
          </div>
        </div>

        {/* ─── Recovery alert · common mistake ─── */}
        <div
          data-testid="cleanup-alert"
          className="mb-10 rounded-2xl border-l-4 border-[#C4636E] bg-[#C4636E]/5 p-6 sm:p-7"
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#C4636E] flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#C4636E] font-sans-display font-bold mb-1.5">
                Has vist "1 error" o "No se ha podido obtener" a Sitemaps?
              </p>
              <h2 className="font-sans-display text-lg sm:text-xl text-[#0B2545] tracking-tight leading-snug mb-3">
                Error habitual: has enganxat URLs d'articles al camp de Sitemaps.
              </h2>
              <p className="text-[#475569] leading-relaxed text-sm mb-4">
                Al Search Console el <strong>camp de Sitemaps només accepta un fitxer XML</strong> (
                <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-slate-200">sitemap.xml</code>
                ). Les URLs individuals d'articles van a un lloc diferent: la <strong>barra d'Inspecció d'URL</strong> a dalt de tot.
              </p>
              <p className="font-sans-display font-semibold text-[#0B2545] text-sm mb-2">
                Com netejar-ho ara (1 minut):
              </p>
              <ol className="list-decimal pl-5 space-y-1.5 marker:text-[#C4636E] marker:font-bold text-sm text-[#475569] mb-4">
                <li>A <strong className="text-[#0B2545]">Sitemaps</strong>, clica cada línia que digui "1 error" o "No se ha podido obtener".</li>
                <li>A dalt a la dreta hi ha els tres punts <span className="inline-block px-1.5 rounded bg-white border border-slate-200 text-slate-700 text-xs font-mono">···</span> → clica <strong>"Retirar sitemap"</strong>.</li>
                <li>Repeteix fins deixar només <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-slate-200">/sitemap.xml</code> (que està "Correcto"). Aquest s'ha de quedar.</li>
                <li>Un cop net, salta al <strong>Pas 2 d'aquesta pàgina</strong> i utilitza els botons <strong>"Copiar i obrir GSC"</strong> — copien la URL al portapapers i obren l'inspector, així només has d'enganxar-la amb Cmd+V.</li>
              </ol>
              <a
                href={`${GSC_BASE}/sitemaps?${GSC_PROPERTY_QUERY}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cleanup-open-sitemaps"
                className="inline-flex items-center gap-2 text-xs font-sans-display font-medium tracking-wide px-3 py-2 bg-white border border-[#C4636E] text-[#C4636E] rounded-md hover:bg-[#C4636E] hover:text-white transition"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Obrir Sitemaps a GSC per esborrar els incorrectes
              </a>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {/* ─── Step 1 · Resubmit sitemap ─── */}
          <StepCard n="1" icon={FileSearch} title="Torna a enviar el sitemap" testid="step-1-sitemap">
            <p>
              Google no re-descobreix el sitemap sol; cal donar-li empenta perquè descobreixi més ràpid els articles amb <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">dateModified</code> actualitzat.
            </p>

            <div className="bg-[#F9F8F6] border border-slate-200 rounded-lg p-4">
              <UrlRow url={SITEMAP_URL} label="Sitemap URL" />
            </div>

            <ol className="list-decimal pl-5 space-y-2 marker:text-[#B4C5A4] marker:font-semibold">
              <li>Obre <strong className="text-[#0B2545]">Search Console → Sitemaps</strong> (menú lateral).</li>
              <li>
                Si <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">sitemap.xml</code> ja hi és, mira l'estat i clica els tres punts <span className="inline-block px-1.5 rounded bg-slate-100 text-slate-700">···</span> → "Torna a analitzar".
              </li>
              <li>Si no hi és, escriu <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">sitemap.xml</code> al camp d'afegir i clica "ENVIAR".</li>
            </ol>

            <a
              href={`${GSC_BASE}/sitemaps?${GSC_PROPERTY_QUERY}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-gsc-sitemaps"
              className="inline-flex items-center gap-2 h-10 px-4 bg-[#0B2545] text-[#F9F8F6] rounded-md hover:bg-[#0a1f3a] transition font-sans-display text-sm font-medium tracking-wide mt-2"
            >
              Obrir Search Console → Sitemaps
              <ExternalLink className="h-4 w-4" />
            </a>
          </StepCard>

          {/* ─── Step 2 · Request indexation ─── */}
          <StepCard n="2" icon={Repeat} title="Sol·licita indexació URL per URL" testid="step-2-indexation">
            <div className="rounded-lg border-2 border-[#C4636E] bg-[#C4636E]/5 p-4 mb-2" data-testid="step-2-warning">
              <p className="font-sans-display font-bold text-[#C4636E] text-sm mb-1 flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4" />
                Compte: aquest pas NO es fa a Sitemaps
              </p>
              <p className="text-sm text-[#475569] leading-relaxed">
                Aquestes URLs són d'articles individuals — <strong>NO les enganxis a "Sitemaps"</strong> (allà només va <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-slate-200">sitemap.xml</code>). Van a la barra d'Inspecció URL a dalt de tot, o més fàcil: fes servir els botons <strong>"Copiar i obrir GSC"</strong> de sota — copien la URL al portapapers i obren l'inspector, així només has d'enganxar-la amb Cmd+V.
              </p>
            </div>

            <p>
              Per als 3 articles nous i el pilar actualitzat cal demanar indexació manual — la "cua" habitual de Google pot trigar setmanes. Amb sol·licitud manual sol baixar a hores/dies.
            </p>

            <p className="font-sans-display font-semibold text-[#0B2545] text-sm">
              Recomanat: fes servir el botó "Copiar i obrir GSC" de cada URL.
            </p>
            <ol className="list-decimal pl-5 space-y-2 marker:text-[#B4C5A4] marker:font-semibold">
              <li>Clica <strong className="text-[#0B2545]">"Copiar i obrir GSC"</strong> al costat de cada URL de sota. Fa dues coses: (a) copia la URL al portapapers, (b) obre l'inspector de GSC en una pestanya nova.</li>
              <li>Un cop a la pestanya de GSC, situa el cursor a la <strong className="text-[#0B2545]">barra de cerca gran de dalt</strong> ("Inspeccionar qualsevol URL"), enganxa amb Cmd+V (Ctrl+V a Windows) i prem Enter.</li>
              <li>Espera l'informe (5-10 segons).</li>
              <li>Si diu "L'URL no és a Google", clica <strong className="text-[#0B2545]">"Sol·licita indexació"</strong>.</li>
              <li>Si ja hi és, clica igualment "Sol·licita indexació" per accelerar la re-visita.</li>
              <li>Repeteix per totes les 8 URLs (4 CA + 4 ES).</li>
            </ol>

            <div className="bg-white border border-slate-200 rounded-lg p-2 divide-y divide-slate-100">
              {NEW_URLS.map((row) => (
                <div key={row.title} className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[9px] font-sans-display font-bold px-1.5 py-0.5 rounded ${
                      row.priority === "P0"
                        ? "bg-[#C4636E]/15 text-[#C4636E]"
                        : "bg-[#B4C5A4]/20 text-[#4A5D3D]"
                    }`}>
                      {row.priority}
                    </span>
                    <p className="text-sm font-sans-display font-semibold text-[#0B2545]">{row.title}</p>
                  </div>
                  <p className="text-xs italic text-slate-400 mb-2">{row.note}</p>
                  <div className="pl-1">
                    <UrlRow url={row.ca} label="CA" inspectable />
                    <UrlRow url={row.es} label="ES" inspectable />
                  </div>
                </div>
              ))}
            </div>

            <a
              href={`${GSC_BASE}/inspect?${GSC_PROPERTY_QUERY}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-gsc-inspect"
              className="inline-flex items-center gap-2 h-10 px-4 bg-[#0B2545] text-[#F9F8F6] rounded-md hover:bg-[#0a1f3a] transition font-sans-display text-sm font-medium tracking-wide mt-2"
            >
              Obrir Search Console → Inspecció URL
              <ExternalLink className="h-4 w-4" />
            </a>

            <p className="text-xs italic text-slate-400 pt-1">
              💡 Consell: GSC té un límit d'aproximadament 10-15 sol·licituds al dia. Fes-ne 4 avui i 4 demà si et queda saturat.
            </p>
          </StepCard>

          {/* ─── Step 3 · Verify hreflang ─── */}
          <StepCard n="3" icon={Globe} title="Verifica que el hreflang CA↔ES està correcte" testid="step-3-hreflang">
            <p>
              Cada article té una versió catalana i una versió castellana amb <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">hreflang</code> que apunta a l'altra idioma. Si Google no ho reconeix, poden aparèixer ambdues versions com si fossin duplicades.
            </p>

            <ol className="list-decimal pl-5 space-y-2 marker:text-[#B4C5A4] marker:font-semibold">
              <li>A GSC, obre <strong className="text-[#0B2545]">Configuració → Cobertura d'idioma internacional (Legacy)</strong> o mira directament l'inspector URL.</li>
              <li>Enganxa la URL catalana d'un article i mira que a "Info addicional" surti la URL castellana com a "alternativa".</li>
              <li>Alternativa més ràpida: fes servir aquest verificador extern (t'assenyala errors visualment).</li>
            </ol>

            <a
              href="https://technicalseo.com/tools/hreflang/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-hreflang-tool"
              className="inline-flex items-center gap-2 h-10 px-4 bg-white border border-[#0B2545] text-[#0B2545] rounded-md hover:bg-[#0B2545] hover:text-[#F9F8F6] transition font-sans-display text-sm font-medium tracking-wide mt-2"
            >
              Obrir Merkle Hreflang Tool (extern)
              <ExternalLink className="h-4 w-4" />
            </a>

            <div className="bg-[#F9F8F6] border-l-2 border-[#B4C5A4] pl-4 py-3 text-sm text-[#475569] italic">
              El que has de veure: cada URL declara <code className="not-italic bg-white px-1.5 py-0.5 rounded text-xs">x-default</code>, <code className="not-italic bg-white px-1.5 py-0.5 rounded text-xs">ca-ES</code> i <code className="not-italic bg-white px-1.5 py-0.5 rounded text-xs">es-ES</code>, i cada versió apunta correctament a l'altra. Si algú no apunta ↔, corregim.
            </div>
          </StepCard>

          {/* ─── Step 4 · Weekly monitoring ─── */}
          <StepCard n="4" icon={BarChart3} title="Monitoratge setmanal (cada dilluns · 5 min)" testid="step-4-weekly">
            <p>
              La feina veritablement estratègica del SEO és mirar cada setmana quines queries porten tràfic real. En 2-3 setmanes hauríeu de començar a veure impressions dels articles nous.
            </p>

            <p className="font-sans-display font-semibold text-[#0B2545]">Rutina de 5 minuts:</p>
            <ol className="list-decimal pl-5 space-y-2 marker:text-[#B4C5A4] marker:font-semibold">
              <li>Obre <strong className="text-[#0B2545]">Rendiment → Cerca a Google</strong>.</li>
              <li>Selecciona rang: "Últims 7 dies" comparat amb "Últims 7 dies anteriors".</li>
              <li>Anota: impressions totals, clics totals, CTR mitjà, posició mitjana.</li>
              <li>Ordena per "Impressions" i mira les <strong className="text-[#0B2545]">top 20 queries</strong>. Compara amb la setmana anterior.</li>
              <li>Filtra per pàgina i mira si cada un dels 4 articles del cluster té impressions.</li>
            </ol>

            <p className="pt-2 font-sans-display font-semibold text-[#0B2545]">Queries objectiu que hem de començar a veure:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(WEEKLY_QUERIES_TO_WATCH).map(([cat, list]) => (
                <div key={cat} className="bg-white border border-slate-200 rounded-lg p-4">
                  <p className="text-[10px] font-sans-display font-bold uppercase tracking-widest text-[#B4C5A4] mb-2">
                    {cat}
                  </p>
                  <ul className="space-y-1.5 text-xs text-[#475569]">
                    {list.map((q) => (
                      <li key={q} className="flex items-start gap-1.5">
                        <ChevronRight className="h-3 w-3 text-[#0B2545] mt-0.5 flex-shrink-0" />
                        <span className="italic">{q}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <p className="font-sans-display font-semibold text-[#0B2545] pt-2">Senyals que estem guanyant:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#B4C5A4] text-sm">
              <li>Impressions setmanals creixen &gt;15% ordre-a-ordre</li>
              <li>Alguna query surt a posició 5-15 (marge per pujar a top 3)</li>
              <li>CTR d'algun article supera el 3%</li>
              <li>Alguna URL rep clics per queries que no havíem pensat (aquí trobem el 5è article!)</li>
            </ul>

            <a
              href={`${GSC_BASE}/performance/search-analytics?${GSC_PROPERTY_QUERY}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-gsc-performance"
              className="inline-flex items-center gap-2 h-10 px-4 bg-[#0B2545] text-[#F9F8F6] rounded-md hover:bg-[#0a1f3a] transition font-sans-display text-sm font-medium tracking-wide mt-2"
            >
              Obrir Search Console → Rendiment
              <ExternalLink className="h-4 w-4" />
            </a>
          </StepCard>

          {/* Extra tools */}
          <section className="bg-[#0B2545] text-[#F9F8F6] rounded-2xl p-7 sm:p-9 relative overflow-hidden">
            <div className="pointer-events-none absolute -bottom-16 -right-16 h-56 w-56 rounded-full bg-[#B4C5A4]/15 blur-3xl" />
            <div className="relative">
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#B4C5A4] font-sans-display font-semibold mb-3">
                Bonus · Eines externes gratuïtes
              </p>
              <h2 className="font-sans-display text-2xl tracking-tight leading-tight mb-4">
                Valida abans que Google indexi.
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <a
                  href="https://search.google.com/test/rich-results"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ext-rich-results"
                  className="block bg-white/5 border border-white/10 rounded-lg p-4 hover:bg-white/10 transition"
                >
                  <p className="font-sans-display font-medium mb-1 flex items-center gap-1.5">
                    Rich Results Test
                    <ExternalLink className="h-3 w-3 opacity-60" />
                  </p>
                  <p className="text-xs text-[#F9F8F6]/60">
                    Comprova si HowTo, FAQ i Article schemas es reconeixen bé.
                  </p>
                </a>
                <a
                  href="https://validator.schema.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ext-schema-validator"
                  className="block bg-white/5 border border-white/10 rounded-lg p-4 hover:bg-white/10 transition"
                >
                  <p className="font-sans-display font-medium mb-1 flex items-center gap-1.5">
                    Schema.org Validator
                    <ExternalLink className="h-3 w-3 opacity-60" />
                  </p>
                  <p className="text-xs text-[#F9F8F6]/60">
                    Valida la sintaxi tècnica de tots els JSON-LD del lloc.
                  </p>
                </a>
                <a
                  href="https://pagespeed.web.dev/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ext-pagespeed"
                  className="block bg-white/5 border border-white/10 rounded-lg p-4 hover:bg-white/10 transition"
                >
                  <p className="font-sans-display font-medium mb-1 flex items-center gap-1.5">
                    PageSpeed Insights
                    <ExternalLink className="h-3 w-3 opacity-60" />
                  </p>
                  <p className="text-xs text-[#F9F8F6]/60">
                    Core Web Vitals + rendiment mòbil (senyal de ranking).
                  </p>
                </a>
                <a
                  href="https://technicalseo.com/tools/hreflang/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ext-hreflang"
                  className="block bg-white/5 border border-white/10 rounded-lg p-4 hover:bg-white/10 transition"
                >
                  <p className="font-sans-display font-medium mb-1 flex items-center gap-1.5">
                    Merkle Hreflang Tool
                    <ExternalLink className="h-3 w-3 opacity-60" />
                  </p>
                  <p className="text-xs text-[#F9F8F6]/60">
                    Valida el mapping hreflang CA ↔ ES sense manuals de GSC.
                  </p>
                </a>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
